import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import { saveTokens } from "../../../../../services/socialTokenService";
import { CognitoAuthService } from "../../../../../services/cognitoService";
import { getAllCustomers } from "../../../../../services/customerService";
import { Customer } from "../../../../../types/customer";
import { Platform } from "../../../../../types/Platforms";
import {
  handleInstagramCallback,
  testInstagramPost,
} from "../services/instagramAuthentication";
import { debug } from "../../../../../utils/debugLogger";
import { validateOAuthState } from "../../../../../utils/oauthState";
import {
  InstagramState,
  InstagramPage,
} from "../types/instagram.types";

const TEST_IMAGE_URL =
  "https://d13ngcb4f5qzyz.cloudfront.net/daisy-ai-1/images/20250308_204058.png";

const InstagramCallback = () => {
  const navigate = useNavigate();
  const { isAuthenticated, idToken } = useAuth();

  const [state, setState] = useState<InstagramState>({
    loading: true,
    error: null,
    accessToken: null,
    userEmail: null,
    selectedCustomer: null,
    selectedPlatform: Platform.INSTAGRAM,
    pages: null,
    connectionSaved: false,
    successMessage: null,
    savingPageId: null,
    testingPage: null,
    connectedPageIds: {},
  });

  const [customersList, setCustomersList] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomerIdByPageId, setSelectedCustomerIdByPageId] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    const processCallback = async () => {
      let code: string | null = null;
      try {
        const urlParams = new URLSearchParams(window.location.search);
        if (!validateOAuthState("instagram", urlParams.get("state"))) {
          throw new Error("Security check failed (invalid OAuth state). Please try connecting again.");
        }
        code = urlParams.get("code");
        if (!code) {
          throw new Error("No authorization code received");
        }
        const cleanCode = code.split("#")[0];
        const response = await handleInstagramCallback(cleanCode);
        setState((prev) => ({
          ...prev,
          accessToken: response.access_token,
          userEmail: response.email || response.pages?.[0]?.name,
          pages: response.pages,
          loading: false,
        }));
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Failed to connect to Instagram";
        setState((prev) => ({
          ...prev,
          error: message,
          loading: false,
        }));
      }
    };
    processCallback();
  }, []);

  useEffect(() => {
    if (!idToken || !state.pages?.length) return;
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const customers = await getAllCustomers(idToken);
        setCustomersList(customers);
      } catch (err) {
        debug.error("Error fetching customers:", err);
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, [idToken, state.pages?.length]);

  const handleConnectPage = async (page: InstagramPage) => {
    const customerId = selectedCustomerIdByPageId[page.id];
    const customer = customersList.find((c) => c.customer_id === customerId);
    if (!customerId || !customer) {
      setState((prev) => ({
        ...prev,
        error: "Select a business profile for this account.",
      }));
      return;
    }
    if (!idToken) return;
    setState((prev) => ({ ...prev, error: null, savingPageId: page.id }));
    try {
      const created_by = CognitoAuthService.getUserEmail(idToken);
      const profile = {
        id: page.instagram_business_account?.id || page.id,
        token: page.access_token,
        metadata: {
          name: page.name,
          category: page.category || "Instagram",
          type: page.instagram_business_account ? "business" : "personal",
        },
      };
      await saveTokens(
        {
          customer_id: customer.customer_id,
          created_by,
          platform: Platform.INSTAGRAM,
          profiles: [profile],
        },
        idToken
      );
      setState((prev) => ({
        ...prev,
        savingPageId: null,
        connectedPageIds: { ...(prev.connectedPageIds || {}), [page.id]: true },
        error: null,
      }));
    } catch (err) {
      debug.error("Error saving connection:", err);
      setState((prev) => ({
        ...prev,
        savingPageId: null,
        error: "Failed to save Instagram connection for this account.",
      }));
    }
  };

  const handleTestPage = async (page: InstagramPage) => {
    if (!page.instagram_business_account) {
      alert("No Instagram business account linked to this page.");
      return;
    }
    setState((prev) => ({ ...prev, testingPage: page.id }));
    try {
      await testInstagramPost({
        accountId: page.instagram_business_account.id,
        accessToken: page.access_token,
        imageUrl: TEST_IMAGE_URL,
        caption: `Test post from Daisy AI! Posted at: ${new Date().toLocaleString()}`,
      });
      alert("Post successful! Check your Instagram account.");
    } catch (err: unknown) {
      const res = err && typeof err === "object" && "response" in err
        ? (err as { response?: { data?: { error?: { code?: number; error_subcode?: number; message?: string } } } }).response?.data?.error
        : null;
      if (res?.code === 25 && res?.error_subcode === 2207050) {
        alert(
          "Your Instagram account appears to be restricted. Please ensure:\n\n" +
            "1. Your Instagram account is a Business or Creator account\n" +
            "2. Your account is not restricted or disabled\n" +
            "3. You've completed Instagram's verification process if required\n" +
            "4. You've accepted Instagram's terms and conditions\n\n" +
            "Try logging into Instagram directly to resolve any account issues."
        );
      } else {
        const msg = res?.message || (err instanceof Error ? err.message : "Unknown error");
        alert(`Failed to post to Instagram: ${msg}`);
      }
    } finally {
      setState((prev) => ({ ...prev, testingPage: null }));
    }
  };

  if (!isAuthenticated) {
    sessionStorage.setItem("instagram_auth_success", "true");
    navigate("/login", {
      state: { returnUrl: "/social-posts?tab=connect" },
    });
    return null;
  }

  const goToConnect = () => navigate("/social-posts?tab=connect");
  const isConnected = (pageId: string) => Boolean(state.connectedPageIds?.[pageId]);

  return (
    <div className="flex flex-col min-h-screen p-4">
      <div className="w-full flex justify-start pl-4 mb-2">
        <button
          type="button"
          onClick={goToConnect}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      </div>
      <div className="flex flex-col items-center w-full flex-1">
        {state.loading ? (
          <div>Loading...</div>
        ) : state.error && !state.pages?.length ? (
          <div className="text-red-500">{state.error}</div>
        ) : (
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <header className="mb-8 pb-8 border-b border-gray-100">
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
                Connect Instagram Business
              </h1>
              <p className="text-sm text-gray-500">
                Manage and connect your Instagram Business accounts.
              </p>
            </header>

            {state.pages && state.pages.length > 0 ? (
              <>
                <div className="mb-4">
                  <h2 className="text-sm font-semibold text-gray-700">
                    Available Instagram Business Accounts
                  </h2>
                </div>
                {state.error && (
                  <p className="text-sm text-red-600 mb-3">{state.error}</p>
                )}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {state.pages.map((page) => {
                    const connected = isConnected(page.id);
                    const saving = state.savingPageId === page.id;
                    const testing = state.testingPage === page.id;
                    const hasBusinessAccount = Boolean(page.instagram_business_account?.id);
                    const displayId = page.instagram_business_account?.id || page.id;
                    return (
                      <div
                        key={page.id}
                        className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col hover:border-brand-primary transition-colors shadow-sm"
                      >
                        <div className="mb-4">
                          <h3 className="text-base font-semibold text-gray-900">
                            {page.name}
                          </h3>
                          {page.instagram_business_account?.name && (
                            <p className="text-sm text-gray-600 mt-0.5">
                              @{page.instagram_business_account.name}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-1">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400 px-2 py-0.5 bg-surface-ui rounded">
                              {hasBusinessAccount ? "Business Account" : "Personal"}
                            </span>
                            <span className="text-[10px] font-mono text-gray-400">
                              ID: {displayId}
                            </span>
                          </div>
                        </div>
                        <div className="mt-auto space-y-3">
                          {connected ? (
                            <p className="text-sm font-medium text-green-700 py-2">
                              Connected
                            </p>
                          ) : (
                            <>
                              <div>
                                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                  Assign Profile
                                </label>
                                <select
                                  value={selectedCustomerIdByPageId[page.id] ?? ""}
                                  onChange={(e) =>
                                    setSelectedCustomerIdByPageId((prev) => ({
                                      ...prev,
                                      [page.id]: e.target.value,
                                    }))
                                  }
                                  disabled={customersLoading}
                                  className="block w-full rounded-lg border border-gray-200 text-xs h-9 px-3 focus:ring-brand-primary focus:border-brand-primary"
                                >
                                  <option value="">
                                    -- Select a Business Profile --
                                  </option>
                                  {customersList.map((c) => (
                                    <option key={c.customer_id} value={c.customer_id}>
                                      {c.business_name}
                                      {c.city && c.state ? ` - ${c.city}, ${c.state}` : ""}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleConnectPage(page)}
                                  disabled={!selectedCustomerIdByPageId[page.id] || saving}
                                  className="flex-1 py-2 text-xs text-white bg-brand-primary rounded-lg font-semibold hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                  {saving ? (
                                    <span className="flex items-center justify-center gap-1">
                                      <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                                      Connecting...
                                    </span>
                                  ) : (
                                    "Connect"
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleTestPage(page)}
                                  disabled={!hasBusinessAccount || testing}
                                  className="px-3 py-2 text-xs rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {testing ? (
                                    <span className="flex items-center gap-1">
                                      <span className="animate-spin inline-block w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full" />
                                      Testing...
                                    </span>
                                  ) : (
                                    "Test"
                                  )}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </section>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={goToConnect}
                    className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
                  >
                    Done
                  </button>
                </div>
              </>
            ) : state.pages?.length === 0 ? (
              <p className="text-gray-500 text-sm">No Instagram business accounts found.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramCallback;
